// src/features/Permissions/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

// Import statusOptions from filter.tsx (do not modify filter.tsx)
import { statusOptions } from "./filter";

const FormSchema = z.object({
  // Strategy autocomplete (display + ID)
  strategy_name: z.preprocess(
    (val: any) => preprocessTextAsyncAutocompleteField(val),
    z
      .union([z.string(), z.object({ id: z.number(), name: z.string() })])
      .optional(),
  ),
  strategy_id: z.coerce.number().optional().nullable(),

  // Admin User autocomplete (display + ID)
  admin_user_email: z.preprocess(
    (val: any) => preprocessTextAsyncAutocompleteField(val),
    z
      .union([z.string(), z.object({ id: z.number(), email: z.string() })])
      .optional(),
  ),
  user_id: z.coerce.number().optional().nullable(),

  // Status select
  status: z.string().nullable().optional(),

  // Numeric field
  max_issuer_count: z.coerce.number().optional().nullable(),
});

export function PermissionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Permissions {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: strategy & admin user */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="strategy_name"
                    label={t(`${table_name}.columns.strategy_name`, {
                      ns: "tables",
                      defaultValue: "Strategy",
                    })}
                    base_url="legacy-quantilia/strategies"
                    column_name="name"
                    id_column="strategy_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="admin_user_email"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Admin User",
                    })}
                    base_url="legacy-quantilia/admin_users"
                    column_name="email"
                    id_column="user_id"
                    control={form.control}
                  />
                </div>
                {/* Row 2: status & max issuer count */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="status"
                    label={t(`${table_name}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    options={statusOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <InputField
                    name="max_issuer_count"
                    label={t(`${table_name}.columns.max_issuer_count`, {
                      ns: "tables",
                      defaultValue: "Max Issuer Count",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["strategy_id", "user_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
